import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../../storage/storage";
import { FiCalendar, FiCheckSquare, FiEdit, FiHome, FiMail, FiUsers, FiSettings, FiUser } from "react-icons/fi";
import { MdOutlineSpaceDashboard, MdOutlineAddHome, MdOutlineSync } from "react-icons/md";
import { TbUsersPlus, TbWebhook } from "react-icons/tb";
import { GoOrganization } from "react-icons/go";
import { FaCheck } from "react-icons/fa";

const Sidebar = ({ isOpen, onClose }) => {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const location = useLocation();
  const [profile, setProfile] = useState(null);

  // Setup-progress widget in the footer reuses the exact same sessionStorage
  // flags Dashboard.jsx already computes completedTasks from — no new data.
  const companyInfo = JSON.parse(sessionStorage.getItem("companyInfo"));
  const PropertyInfo = JSON.parse(sessionStorage.getItem("PropertyData"));
  const CleanerInfo = JSON.parse(sessionStorage.getItem("CleanerData"));
  const completedTasks = [companyInfo, PropertyInfo, CleanerInfo].filter(Boolean).length;
  const totalTasks = 3;
  const isSetupComplete = completedTasks === totalTasks;
  const setupPercentage = Math.round((completedTasks / totalTasks) * 100);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/profile/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => setProfile(response.data))
      .catch(() => {
        // Decorative — silently skip if it fails, don't block the sidebar.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initials = profile
    ? `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase()
    : "";
  const fullName = profile ? `${profile.first_name} ${profile.last_name}`.trim() : "";

  const menuItems = [
    { 
      name: "Dashboard", 
      path: "/dashboard", 
      icon: <MdOutlineSpaceDashboard />,
      badge: null
    },
    {
      name: "Upcoming Stays",
      path: "/stays",
      icon: <FiCalendar />,
      badge: null
    },
    {
      name: "Ad Hoc Tasks",
      path: "/other-tasks",
      icon: <FiCheckSquare />,
      badge: null
    },
    {
      name: "My Properties",
      path: "/settings/properties",
      icon: <FiHome />,
      badge: null,
      tab: "Properties"
    },
    {
      name: "My Cleaners",
      path: "/settings/cleaners",
      icon: <GoOrganization />,
      badge: null,
      tab: "Cleaners"
    },
    {
      name: "My Invitations",
      path: "/send-invitation",
      icon: <FiMail />,
      badge: null
    },
    { type: "divider" },
    {
      name: "Add Properties",
      path: "/property",
      icon: <MdOutlineAddHome />,
      badge: null
    },
    {
      name: "Invite Cleaners",
      path: "/invite-cleaner",
      icon: <TbUsersPlus />,
      badge: null
    },
    { type: "divider" },
    {
      name: "Manage Cleaner Tasks",
      path: "/task",
      icon: <FiEdit />,
      badge: null
    },
    {
      name: "My Profile",
      path: "/settings/profile",
      icon: <FiUser />,
      badge: null,
      tab: "Profile"
    },
    {
      name: "My Companies",
      path: "/settings/company",
      icon: <GoOrganization />,
      badge: null,
      tab: "Company"
    },
    { type: "divider" },
    {
      name: "System Change Logs",
      path: "/webhook-logs",
      icon: <TbWebhook />,
      badge: null
    },
    {
      name: "Auto-Scheduled Task Logs",
      path: "/cron-job-logs",
      icon: <MdOutlineSync />,
      badge: null
    },
    { type: "divider" },
     {
      name: "Settings",
      path: "/settings/profile",
      icon: <FiSettings />,
      tab: "Profile"
    },
  ];

  const isOtherTasksActive =
    location.pathname.startsWith("/other-tasks") ||
    location.pathname.startsWith("/other-tasksheets/") ||
    location.pathname.startsWith("/task-invoice/");

  return (
    <div className="w-64 h-full flex flex-col shadow-lg bg-[#586989] overflow-hidden">
      {/* Logo Section */}
      <div className="p-6 border-b border-white/15 flex-shrink-0">
        <div className="flex items-center space-x-3">
          {/* The image asset itself has icon + wordmark stacked together,
              so it's kept small (mostly reads as just the icon glyph at
              this size) inside a light box for contrast against the dark
              sidebar, with a separate crisp "HouseKeeper" label alongside
              it rather than relying on the tiny embedded text. */}
          <div className="bg-[#FFF5CE] rounded-xl p-2 flex-shrink-0">
            <img
              src="/images/main-logo.jpg"
              alt="HouseKeeper Logo"
              className="h-8 w-auto"
            />
          </div>
          <h1 className="text-lg font-bold text-white">HouseKeeper</h1>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto sidebar-scrollbar">
        {menuItems.map((item, index) => {
          if (item.type === "divider") {
            return (
              <div key={`divider-${index}`} className="border-t border-white/15 my-2"></div>
            );
          }

          const isActive =
            location.pathname === item.path ||
            (item.path === "/other-tasks" && isOtherTasksActive) ||
            (item.path.startsWith("/settings") && location.pathname.startsWith("/settings") && location.pathname === item.path);

          return (
            <NavLink
              key={item.path}
              to={{
                pathname: item.path
              }}
              onClick={() => {
                if (item.path === "/property") {
                  sessionStorage.removeItem("edit");
                }
                if (item.tab) {
                  // Store the active tab in sessionStorage for the Settings page to read
                  sessionStorage.setItem("settingsActiveTab", item.tab);
                }
                if (onClose) onClose();
              }}
              className={`
                flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 border-l-4
                ${isActive
                  ? "bg-white/15 border-[#FFF5CE] text-white font-semibold"
                  : "border-transparent text-[#e4e7f0] hover:bg-white/10 hover:text-white"
                }
              `}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="flex-1 text-sm font-medium">{item.name}</span>
              {item.badge && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer: setup progress + user identity — tinted as one distinct
          zone (rather than just a border) so it doesn't read as loose
          floating pieces under the nav list. */}
      <div className="flex-shrink-0 border-t border-white/15 bg-black/10 p-4 space-y-3">
        {isSetupComplete ? (
          <div className="flex items-center gap-2 bg-white/10 text-[#FFF5CE] rounded-lg px-3 py-2 border border-white/15">
            <FaCheck className="text-sm flex-shrink-0" />
            <span className="text-sm font-medium">Setup complete</span>
          </div>
        ) : (
          <div className="bg-white/10 border border-white/15 rounded-lg px-3 py-2.5">
            <div className="flex items-center justify-between text-xs text-[#e4e7f0] mb-1">
              <span>Setup progress</span>
              <span className="font-semibold text-white">{setupPercentage}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-[#FFF5CE] transition-all duration-300"
                style={{ width: `${setupPercentage}%` }}
              ></div>
            </div>
            <p className="text-xs text-[#c7ccdb] mt-1">
              {completedTasks} of {totalTasks} steps complete
            </p>
          </div>
        )}

        {profile && (
          <div className="flex items-center gap-3 pt-1">
            <div className="w-9 h-9 rounded-full bg-[#FFF5CE] text-[#586989] flex items-center justify-center text-sm font-bold flex-shrink-0 ring-2 ring-white/10">
              {initials || <FiUser />}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-white font-medium truncate">{fullName}</p>
              <p className="text-xs text-[#c7ccdb]">Property owner</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;